import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Badge,
  Avatar,
  Chip,
  Button,
  Paper,
  IconButton,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { AppLayoutContext } from '../../providers/AppLayout'
import { NotificationContext } from '../../providers/NotificationContext'
import { Notification } from '../../models/Notification.model'
import HospitalIcon from '../../icons/HospitalIcon'
import AgencyIcon from '../../icons/AgencyIcon'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import SearchIcon from '@mui/icons-material/Search'
import RateReviewIcon from '@mui/icons-material/RateReview'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BusinessIcon from '@mui/icons-material/Business'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import MailIcon from '@mui/icons-material/Mail'
import DraftsIcon from '@mui/icons-material/Drafts'
import InboxIcon from '@mui/icons-material/Inbox'

type NotificationTypeTab = 'reported_review' | 'claim_page' | 'new_organization_request' | 'all'

const Notifications = (): JSX.Element => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = React.useContext(NotificationContext)
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState<'all' | 'hospital' | 'agency'>('all')
  const [selectedTypeTab, setSelectedTypeTab] = useState<NotificationTypeTab>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)

  React.useEffect(() => {
    AppLayoutContext.setPageName('Notifications')
  }, [])

  // Reset type tab when organization tab changes
  React.useEffect(() => {
    setSelectedTypeTab('all')
  }, [selectedTab])

  // All backend type values that represent any kind of organization request
  const ORG_REQUEST_TYPES = ['new_organization_request', 'new_hospital_request', 'new_agency_request']
  const CLAIM_NOTIF_TYPES = [
    'claim_page',
    'claim_request_submitted',
    'claim_request_approved',
    'claim_request_rejected',
    'claim_request_withdrawn',
  ]

  // Resolve the effective type
  const getNotifType = (notif: Notification): string | undefined =>
    notif.metadata?.type || notif.type

  const isOrgRequestNotif = (notif: Notification): boolean =>
    ORG_REQUEST_TYPES.includes(getNotifType(notif) ?? '')
  const isClaimNotif = (notif: Notification): boolean =>
    CLAIM_NOTIF_TYPES.includes(getNotifType(notif) ?? '')

  // Resolve organization type
  const getOrgType = (notif: Notification): 'hospital' | 'agency' | undefined => {
    if (notif.metadata?.pageType === 'hospital') return 'hospital'
    if (notif.metadata?.pageType === 'agency') return 'agency'
    if (notif.metadata?.claimPageType === 'hospital') return 'hospital'
    if (notif.metadata?.claimPageType === 'agency') return 'agency'
    if (notif.metadata?.organizationType === 'hospital') return 'hospital'
    if (notif.metadata?.organizationType === 'agency') return 'agency'
    const notifType = getNotifType(notif)
    if (notifType === 'new_hospital_request') return 'hospital'
    if (notifType === 'new_agency_request') return 'agency'
    return undefined
  }

  // Get notification category config
  const getNotifCategoryConfig = (notif: Notification) => {
    const type = getNotifType(notif) ?? ''
    if (type === 'reported_review') {
      return { label: 'Reported Review', color: '#FF7043', icon: <RateReviewIcon sx={{ fontSize: 16 }} /> }
    }
    if (CLAIM_NOTIF_TYPES.includes(type)) {
      return { label: 'Claim Page', color: '#FFB300', icon: <AssignmentIcon sx={{ fontSize: 16 }} /> }
    }
    if (ORG_REQUEST_TYPES.includes(type)) {
      return { label: 'New Request', color: '#9C27B0', icon: <BusinessIcon sx={{ fontSize: 16 }} /> }
    }
    return { label: 'Notification', color: '#607D8B', icon: <NotificationsActiveIcon sx={{ fontSize: 16 }} /> }
  }

  const baseNotifications = useMemo(() => notifications, [notifications])

  // Filter notifications by organization type, notification type, search, unread
  const filteredNotifications = useMemo(() => {
    let filtered = baseNotifications

    // Filter by organization type (hospital/agency)
    if (selectedTab !== 'all') {
      filtered = filtered.filter((notif) => getOrgType(notif) === selectedTab)
    }

    // Filter by notification type
    if (selectedTypeTab !== 'all') {
      if (selectedTypeTab === 'new_organization_request') {
        filtered = filtered.filter(isOrgRequestNotif)
      } else if (selectedTypeTab === 'claim_page') {
        filtered = filtered.filter(isClaimNotif)
      } else {
        filtered = filtered.filter((notif) => getNotifType(notif) === selectedTypeTab)
      }
    }

    // Filter by search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.message.toLowerCase().includes(q) ||
          (n.metadata?.organizationName || '').toLowerCase().includes(q),
      )
    }

    // Filter unread only
    if (showUnreadOnly) {
      filtered = filtered.filter((n) => !n.read)
    }

    return filtered
  }, [baseNotifications, selectedTab, selectedTypeTab, searchQuery, showUnreadOnly])

  // Group by organization type
  const hospitalNotifications = useMemo(
    () => baseNotifications.filter((n) => getOrgType(n) === 'hospital'),
    [baseNotifications],
  )
  const agencyNotifications = useMemo(
    () => baseNotifications.filter((n) => getOrgType(n) === 'agency'),
    [baseNotifications],
  )

  const unreadHospitalCount = hospitalNotifications.filter((n) => !n.read).length
  const unreadAgencyCount = agencyNotifications.filter((n) => !n.read).length

  // Get counts for each notification type
  const getTypeCount = (type: NotificationTypeTab): number => {
    let filteredBase = baseNotifications
    if (selectedTab !== 'all') {
      filteredBase = filteredBase.filter((n) => getOrgType(n) === selectedTab)
    }
    if (type === 'all') return filteredBase.filter((n) => !n.read).length
    if (type === 'new_organization_request') return filteredBase.filter((n) => !n.read && isOrgRequestNotif(n)).length
    if (type === 'claim_page') return filteredBase.filter((n) => !n.read && isClaimNotif(n)).length
    return filteredBase.filter((n) => !n.read && getNotifType(n) === type).length
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) {
      const m = Math.floor(diffInSeconds / 60)
      return `${m}m ago`
    }
    if (diffInSeconds < 86400) {
      const h = Math.floor(diffInSeconds / 3600)
      return `${h}h ago`
    }
    if (diffInSeconds < 604800) {
      const d = Math.floor(diffInSeconds / 86400)
      return `${d}d ago`
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const handleNotificationClick = async (notification: Notification) => {
    await markAsRead(notification.id)
    const notifType = getNotifType(notification)
    const orgType = getOrgType(notification) || 'hospital'

    if (notifType === 'reported_review' && notification.metadata?.reviewId) {
      navigate(`/admin/reviews?reviewId=${notification.metadata.reviewId}`, { replace: false })
    } else if (isOrgRequestNotif(notification)) {
      navigate(orgType === 'agency' ? '/admin/agency-requests' : '/admin/hospital-requests')
    } else if (notifType === 'claim_request_withdrawn') {
      const claimPageType = notification.metadata?.pageType || notification.metadata?.claimPageType || 'hospital'
      navigate(`/admin/claim-pages?type=${claimPageType}&status=withdrawn`, { replace: false })
    } else if (isClaimNotif(notification)) {
      const claimPageType = notification.metadata?.pageType || notification.metadata?.claimPageType || 'hospital'
      navigate(`/admin/claim-pages?type=${claimPageType}`, { replace: false })
    } else if (notifType === 'customization_approved' || notifType === 'customization_rejected' || notifType === 'page_customization_proposed') {
      navigate('/admin/page-customizations', { replace: false })
    }
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsRead()
  }

  // Type filter config
  const typeFilters: { key: NotificationTypeTab; label: string; icon: JSX.Element; color: string }[] = [
    { key: 'all', label: 'All', icon: <NotificationsActiveIcon sx={{ fontSize: 18 }} />, color: '#007C91' },
    { key: 'reported_review', label: 'Reviews', icon: <RateReviewIcon sx={{ fontSize: 18 }} />, color: '#FF7043' },
    { key: 'claim_page', label: 'Claims', icon: <AssignmentIcon sx={{ fontSize: 18 }} />, color: '#FFB300' },
    {
      key: 'new_organization_request',
      label: 'Requests',
      icon: <BusinessIcon sx={{ fontSize: 18 }} />,
      color: '#9C27B0',
    },
  ]

  // Org filter tabs config
  const orgTabs = [
    { key: 'all' as const, label: 'All', count: baseNotifications.filter((n) => !n.read).length },
    { key: 'hospital' as const, label: 'Hospitals', count: unreadHospitalCount, icon: <HospitalIcon /> },
    { key: 'agency' as const, label: 'Agencies', count: unreadAgencyCount, icon: <AgencyIcon /> },
  ]

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── Header Banner ── */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background:
            unreadCount > 0
              ? 'linear-gradient(135deg, #007C91 0%, #00ACC1 50%, #26C6DA 100%)'
              : 'linear-gradient(135deg, #546E7A 0%, #78909C 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', right: -30, top: -30, opacity: 0.07 }}>
          {unreadCount > 0 ? (
            <NotificationsActiveIcon sx={{ fontSize: 240 }} />
          ) : (
            <NotificationsNoneIcon sx={{ fontSize: 240 }} />
          )}
        </Box>
        <Box sx={{ position: 'relative' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'flex-start' }} spacing={1.5} sx={{ mb: 1 }}>
            <Box>
              <Typography sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                Notifications
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                {unreadCount > 0
                  ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
                  : "You're all caught up!"}
              </Typography>
            </Box>
            {unreadCount > 0 && (
              <Button
                variant="contained"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllAsRead}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  backdropFilter: 'blur(8px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  boxShadow: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                Mark All Read
              </Button>
            )}
          </Stack>

          {/* Organization toggle */}
          <Box
            sx={{
              display: 'inline-flex',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              mt: 1,
            }}
          >
            {orgTabs.map((tab) => (
              <Button
                key={tab.key}
                startIcon={tab.icon}
                onClick={() => setSelectedTab(tab.key)}
                sx={{
                  px: 2.5,
                  py: 0.75,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: selectedTab === tab.key ? '#007C91' : 'rgba(255,255,255,0.85)',
                  bgcolor: selectedTab === tab.key ? 'rgba(255,255,255,0.95)' : 'transparent',
                  '&:hover': {
                    bgcolor: selectedTab === tab.key ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.1)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                {tab.label}
                {tab.count > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      bgcolor:
                        selectedTab === tab.key ? 'rgba(0,124,145,0.12)' : 'rgba(255,255,255,0.25)',
                      color: selectedTab === tab.key ? '#007C91' : 'white',
                    }}
                  >
                    {tab.count}
                  </Box>
                )}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Type Filters ── */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          {typeFilters.map((tf) => {
            const count = getTypeCount(tf.key)
            const isActive = selectedTypeTab === tf.key
            return (
              <Chip
                key={tf.key}
                icon={React.cloneElement(tf.icon, {
                  sx: { fontSize: '18px !important', color: isActive ? 'white !important' : `${tf.color} !important` },
                })}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{tf.label}</span>
                    {count > 0 && (
                      <Box
                        component="span"
                        sx={{
                          px: 0.6,
                          py: 0.05,
                          borderRadius: 0.75,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: isActive ? 'rgba(255,255,255,0.3)' : `${tf.color}18`,
                          color: isActive ? 'white' : tf.color,
                          lineHeight: 1.4,
                        }}
                      >
                        {count}
                      </Box>
                    )}
                  </Stack>
                }
                onClick={() => setSelectedTypeTab(tf.key)}
                sx={{
                  bgcolor: isActive ? tf.color : 'transparent',
                  color: isActive ? 'white' : '#546E7A',
                  border: isActive ? 'none' : '1px solid rgba(0,0,0,0.12)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  height: 42,
                  px: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '& .MuiChip-icon': { ml: 0.75 },
                  '& .MuiChip-label': { px: 1.25 },
                  '&:hover': {
                    bgcolor: isActive ? tf.color : `${tf.color}10`,
                    transform: 'scale(1.02)',
                  },
                }}
              />
            )
          })}
        </Stack>

      {/* ── Search + Read-state Segmented Control ── */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          fullWidth
          placeholder="Search notifications…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.5,
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              '& fieldset': { borderColor: 'rgba(0,124,145,0.12)' },
              '&:hover fieldset': { borderColor: 'rgba(0,124,145,0.3)' },
              '&.Mui-focused fieldset': { borderColor: '#007C91' },
            },
          }}
        />
        <ToggleButtonGroup
          value={showUnreadOnly ? 'unread' : 'all'}
          exclusive
          onChange={(_, val) => {
            if (val !== null) setShowUnreadOnly(val === 'unread')
          }}
          size="small"
          sx={{
            bgcolor: '#F5F7FA',
            borderRadius: 2.5,
            border: '1px solid rgba(0,124,145,0.12)',
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '10px !important',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              px: 2,
              py: 0.6,
              color: '#90A4AE',
              transition: 'all 0.25s ease',
              '&.Mui-selected': {
                bgcolor: '#007C91',
                color: 'white',
                boxShadow: '0 2px 8px rgba(0,124,145,0.3)',
                '&:hover': { bgcolor: '#00697D' },
              },
              '&:hover': { bgcolor: 'rgba(0,124,145,0.06)' },
            },
          }}
        >
          <ToggleButton value="all" disableRipple>
            <InboxIcon sx={{ fontSize: 17, mr: 0.5 }} />
            All
          </ToggleButton>
          <ToggleButton value="unread" disableRipple>
            <MailIcon sx={{ fontSize: 17, mr: 0.5 }} />
            Unread
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* ── Notification Cards ── */}
      {filteredNotifications.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            background: 'linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 100%)',
            borderRadius: 4,
            border: '2px dashed rgba(0,124,145,0.12)',
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: unreadCount > 0 ? '#E3F2FD' : '#E8F5E9',
              color: unreadCount > 0 ? '#1976D2' : '#43A047',
              mx: 'auto',
              mb: 2.5,
            }}
          >
            {unreadCount > 0 ? (
              <SearchIcon sx={{ fontSize: 36 }} />
            ) : (
              <CheckCircleIcon sx={{ fontSize: 36 }} />
            )}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', mb: 1 }}>
            {searchQuery.trim()
              ? 'No Matching Notifications'
              : showUnreadOnly
                ? 'No Unread Notifications'
                : 'No Notifications'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#78909C', maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
            {searchQuery.trim()
              ? `No notifications matching "${searchQuery.trim()}" were found.`
              : showUnreadOnly
                ? 'All your notifications have been read. Toggle off the unread filter to see all.'
                : "You don't have any notifications yet. They'll appear here when something happens."}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {filteredNotifications.map((notification) => {
            const orgType = getOrgType(notification)
            const categoryConfig = getNotifCategoryConfig(notification)

            return (
              <Card
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: notification.read
                    ? '1px solid rgba(0,0,0,0.06)'
                    : '1px solid rgba(0,124,145,0.25)',
                  borderLeft: notification.read
                    ? '4px solid rgba(0,0,0,0.08)'
                    : `4px solid #007C91`,
                  boxShadow: notification.read
                    ? '0 1px 4px rgba(0,0,0,0.04)'
                    : '0 2px 12px rgba(0,124,145,0.12)',
                  bgcolor: notification.read ? '#FFFFFF' : 'rgba(0,124,145,0.02)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,124,145,0.15)',
                    '& .notif-arrow': { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    {/* Avatar */}
                    <Box sx={{ flexShrink: 0 }}>
                      {notification.metadata?.organizationImageURL ? (
                        <Avatar
                          src={notification.metadata.organizationImageURL}
                          alt={notification.metadata.organizationName || 'Organization'}
                          sx={{
                            width: 48,
                            height: 48,
                            border: notification.read
                              ? '2px solid rgba(0,0,0,0.08)'
                              : '2px solid #007C91',
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: orgType === 'agency' ? '#E0F7FA' : '#E0F2F1',
                            color: orgType === 'agency' ? '#00838F' : '#00695C',
                          }}
                        >
                          {orgType === 'agency' ? (
                            <AgencyIcon sx={{ fontSize: 24 }} />
                          ) : (
                            <HospitalIcon sx={{ fontSize: 24 }} />
                          )}
                        </Avatar>
                      )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Title row */}
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: notification.read ? 500 : 700,
                            color: '#1A2027',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.read && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#007C91',
                              flexShrink: 0,
                              boxShadow: '0 0 0 2px rgba(0,124,145,0.2)',
                            }}
                          />
                        )}
                      </Stack>

                      {/* Message */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#546E7A',
                          mb: 1.5,
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {notification.message}
                      </Typography>

                      {/* Metadata chips + time */}
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {/* Category chip */}
                        <Chip
                          size="small"
                          icon={categoryConfig.icon}
                          label={categoryConfig.label}
                          sx={{
                            bgcolor: `${categoryConfig.color}14`,
                            color: categoryConfig.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                            '& .MuiChip-icon': { color: categoryConfig.color },
                          }}
                        />

                        {/* Org type chip */}
                        {orgType && (
                          <Chip
                            size="small"
                            label={orgType === 'hospital' ? 'Hospital' : 'Agency'}
                            sx={{
                              bgcolor: orgType === 'hospital' ? '#E0F2F1' : '#E0F7FA',
                              color: orgType === 'hospital' ? '#00695C' : '#00838F',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                          />
                        )}

                        {/* Organization name */}
                        {notification.metadata?.organizationName && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#007C91',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          >
                            {notification.metadata.organizationName}
                          </Typography>
                        )}

                        {/* Spacer */}
                        <Box sx={{ flex: 1 }} />

                        {/* Time */}
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AccessTimeIcon sx={{ fontSize: 13, color: '#B0BEC5' }} />
                          <Typography variant="caption" sx={{ color: '#90A4AE', fontSize: '0.75rem' }}>
                            {formatDate(notification.createdAt)}
                          </Typography>
                        </Stack>

                        {notification.read && (
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            <CheckCircleIcon sx={{ fontSize: 13, color: '#A5D6A7' }} />
                            <Typography variant="caption" sx={{ color: '#A5D6A7', fontSize: '0.7rem' }}>
                              Read
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Box>

                    {/* Arrow */}
                    <ArrowForwardIcon
                      className="notif-arrow"
                      sx={{
                        color: '#007C91',
                        opacity: 0.25,
                        fontSize: 20,
                        mt: 0.5,
                        transition: 'all 0.3s ease',
                        transform: 'translateX(-4px)',
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

export default Notifications
